import * as React from "react";
import { Typography, Stack, Modal, Box, Button, Grid, Divider, Link, useMediaQuery } from "@mui/material";
import moment from "moment";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import CloseModalIcon from "../../icons/RoundCloseIcon.svg";
import SpinnerModal from "../../shared/Modal/SpinnerModal";
import { selectPaymentState, selectSkipAutopayResponse, getCancelAutoPay, getAutoPays, clearCancelAutoPaySuccess, selectAutoPays } from "../../resui/payments/sagas/residentpaymentsSlice";
import formatNumberWithCommas from "../util/formatWithCommas";
import { PaidByEnum, ScheduledAutoPayDtoFrequencyEnum, TieredAutopayFeeDto, FlatAutopayFeeDto } from "@inosio/payments-sdk";
import NumberFormat from "react-number-format";
import { TieredPricingTable } from "../../pmui/onboarding/pricingCard";

const useStyles = makeStyles(() => ({
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "space-between",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  wrapper: {
    margin: "1px",
    position: "relative",
  },
  buttonProgress: {
    color: "#053742",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  globalError: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "15px",
    borderTop: "2px  solid #DB3939",
    borderRadius: "24px",
    padding: "8px 20px",
    width: "100%",
    height: "39px",
  },
  gridRows: {
    marginBottom: "4%",
  },
  paymentSummaryText: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "#F1F0F5",
    borderRadius: "24px",
    height: "62px",
    margin: "10px 0",
    padding: "10px",
  },
  termsAndConditionLink: {
    fontFamily: "Work Sans",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    color: "#39A2DB !important",
    marginTop: "18px",
    cursor: "pointer",
  },
}));

interface viewRecurringDetailsModalProps {
  openModal: boolean;
  handleCloseModal: () => void;
  selectedAutoPay: any;
  openAutopayDetails: boolean;
}

const ViewRecurringDetailsModal = (props: viewRecurringDetailsModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const [cancelAutoPayGlobalError, setCancelAutoPayGlobalError] = React.useState<string | null>(null);
  const [isOpenCancelModal, setIsOpenCancelModal] = React.useState(false);
  const [showTieredPricingTable, setShowTieredPricingTable] = React.useState(false);
  const matchesMobile = useMediaQuery("(max-width:600px)");

  const skipUnSkipAutopayDetails = useSelector(selectSkipAutopayResponse);
  const { isSkipAutoPaySuccess, isCancelAutoPaySuccess, cancelAutoPayError, isCancelAutoPayError } = useSelector(selectPaymentState);
  const fetchedAutoPays = useSelector(selectAutoPays);
  const tieredFeeSchedule = Object.keys(props.selectedAutoPay.FeeSchedule).includes("AmountTiers") && props.selectedAutoPay.FeeSchedule.AmountTiers ? (props.selectedAutoPay.FeeSchedule as TieredAutopayFeeDto) : ({} as TieredAutopayFeeDto);
  const flatFeeSchedule = Object.keys(props.selectedAutoPay.FeeSchedule).includes("Amount") ? (props.selectedAutoPay.FeeSchedule as FlatAutopayFeeDto) : ({} as FlatAutopayFeeDto);

  const handleOpenCancelModal = () => setIsOpenCancelModal(true);
  const handleCloseCancelModal = () => setIsOpenCancelModal(false);

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
    dispatch(getAutoPays());
  }, [dispatch]);


  const handleCloseViewAutoPayModal = React.useCallback(() => {
    if (isSkipAutoPaySuccess) dispatch(getAutoPays());
    props.handleCloseModal();
  }, [dispatch, isSkipAutoPaySuccess, props, handleCloseSpinnerModal]);


  const handleCancelAutoPay = () => {
    setIsSpinnerModalOpen(true);
    dispatch({
      type: getCancelAutoPay.type,
      autopayUid: isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.ApUuid : props.selectedAutoPay?.ApUuid,
    });
  };

  React.useEffect(() => {
    if (isCancelAutoPaySuccess) {
      setSpinnerStatus("stop");
      dispatch({
        type: clearCancelAutoPaySuccess.type,
      });
    }
    if (isCancelAutoPayError) {
      setCancelAutoPayGlobalError(cancelAutoPayError);
      handleCloseSpinnerModal();
      handleCloseCancelModal();
    }
  }, [isCancelAutoPaySuccess, dispatch, handleCloseViewAutoPayModal, props, isCancelAutoPayError, cancelAutoPayError, spinnerStatus, handleCloseSpinnerModal]);

  const leftColumnSize = 7;
  const rightColumnSize = 5;
  const frequencyEnumValue = isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.Frequency: props.selectedAutoPay?.Frequency;
  const frequency =
    frequencyEnumValue === ScheduledAutoPayDtoFrequencyEnum.M
      ? "month"
      : frequencyEnumValue === ScheduledAutoPayDtoFrequencyEnum.Q
      ? "3 months"
      : frequencyEnumValue === ScheduledAutoPayDtoFrequencyEnum.S
      ? "6 months"
      : "year";

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: matchesMobile ? "100%" : "472px",
    height: matchesMobile ? "100%" : "560px",
    background: "#FFFFFF",
    boxShadow: "0px 0px 40px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
    borderRadius: matchesMobile ? "0px" : "24px",
    paddingTop: "25px",
    paddingBottom: "1.5%",
    paddingLeft: "16px",
    paddingRight: "16px",
  };

  return (
    <>
      <Modal open={props.openModal} onClose={handleCloseViewAutoPayModal} sx={{ p: 0 }}>
        <Box sx={style}>
          {isSpinnerModalOpen ? (
            <SpinnerModal isInvisible={true} width={"472px"} height={"560px"} openSpinnerModal={isSpinnerModalOpen} handleCloseSpinnerModal={handleCloseSpinnerModal} spinnerStatus={spinnerStatus} closeCancel={props.handleCloseModal} />
          ) : (
            <></>
          )}
          <Stack direction="row" style={{ marginBottom: "15px" }}>
            <Typography id="payment-method-title" sx={{ width: "400px" }}>
              Recurring Payment Details{" "}
            </Typography>

            <div>
              <img src={CloseModalIcon} alt="close" style={{ cursor: "pointer" }} onClick={handleCloseViewAutoPayModal} />
            </div>
          </Stack>

          {cancelAutoPayGlobalError?.length ? (
            <div className={classes.globalError}>
              <Typography id="top-level-error-text">{cancelAutoPayGlobalError}</Typography>
            </div>
          ) : (
            ""
          )}

          {fetchedAutoPays && fetchedAutoPays.length > 0 && props.openAutopayDetails && (
            <div className={classes.globalError}>
              <Typography id="top-level-error-text">You cannot make multiple recurring payments</Typography>
            </div>
          )}

          <Box className={classes.paymentSummaryText}>
            <Typography
              style={{
                color: "#14142B",
                fontSize: "11px",
              }}
              id="paymentSummaryTitleText"
            >
              {" "}
              {props.selectedAutoPay.Type === "Fixed"
                ? `Your payment will execute on ${moment(
                    isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails.NextScheduledPaymentDate : props.selectedAutoPay?.NextScheduledPaymentDate
                  ).format("MM/DD/Y")} and every ${frequency} thereafter for $${formatNumberWithCommas(
                    parseFloat(((props.selectedAutoPay.FixedAmount ?? 0) / 100).toString()) + (flatFeeSchedule.PaidBy === PaidByEnum.Payer ? flatFeeSchedule.Amount / 100 ?? 0 : 0)
                  )} 
          ${!props.selectedAutoPay.EndDate ? "until you cancel it" : `with the final payment on ${moment(props.selectedAutoPay.EndDate).format("MM/DD/Y")}`}`
                : `Your payment will execute on ${moment(
                    isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails.NextScheduledPaymentDate : props.selectedAutoPay?.NextScheduledPaymentDate
                  ).format("MM/DD/Y")} and every ${frequency} thereafter for your full balance on file ${
                    parseFloat(((props.selectedAutoPay.VariableMaxAmountAllowed ?? 0) / 100).toString()) === 0 ? "" : ` up to $${formatNumberWithCommas(parseFloat(((props.selectedAutoPay.VariableMaxAmountAllowed ?? 0) / 100).toString()))}`
                  }  + fee ${!props.selectedAutoPay.EndDate ? "until you cancel it" : `with the final payment on ${moment(props.selectedAutoPay.EndDate).format("MM/DD/Y")}`}`}
            </Typography>
          </Box>

          <Divider />

          <Box className="main-content" sx={{ p: 2, ".MuiTypography-root": { fontFamily: "Work Sans" } }} position={"relative"}>
            {showTieredPricingTable && (
              <Box sx={{ width: "230px", height: "max-content", position: "absolute", top: "-15%", right: "2%", zIndex: "9", background: "#ffffff", border: "2px solid #eeeeee", borderRadius: "5px" }}>
                <TieredPricingTable tiers={tieredFeeSchedule.AmountTiers} />
              </Box>
            )}

            <Grid container className={classes.gridRows}>
              <Grid item xs={leftColumnSize}>
                <Typography id="bold-header">{`Next Payment on ${moment(
                  isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails.NextScheduledPaymentDate : props.selectedAutoPay?.NextScheduledPaymentDate
                ).format("MM/DD/YY")}`}</Typography>
              </Grid>
              <Grid item xs={rightColumnSize}>
              </Grid>
            </Grid>
            <Grid container className={classes.gridRows}>
              <Grid item xs={leftColumnSize}>
                <Typography id="bold-header">Amount</Typography>
              </Grid>
              <Grid item xs={rightColumnSize}>
                <Typography>
                  {props.selectedAutoPay.Type === "Fixed"
                    ? `${
                        "$" +
                        ((isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.Type : props.selectedAutoPay?.Type) === "Fixed"
                          ? formatNumberWithCommas(
                              (
                                ((isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.FixedAmount : props.selectedAutoPay?.FixedAmount)
                                  ? isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0
                                    ? skipUnSkipAutopayDetails?.FixedAmount
                                    : props.selectedAutoPay?.FixedAmount
                                  : 0) / 100
                              ).toFixed(2)
                            )
                          : "Full Balance")
                      }`
                    : "Full Balance"}
                </Typography>
              </Grid>
            </Grid>
            <Grid container className={classes.gridRows}>
              <Grid item xs={leftColumnSize}>
                <Typography id="bold-header">Fee Amount</Typography>
              </Grid>
              <Grid item xs={rightColumnSize}>
                <Typography>
                  {props.selectedAutoPay.Type === "Variable" && tieredFeeSchedule.AmountTiers ? (
                    tieredFeeSchedule.PaidBy === PaidByEnum.Payer ? (
                      <Link
                        className={classes.termsAndConditionLink}
                        onMouseEnter={() => {
                          setShowTieredPricingTable(true);
                        }}
                        onMouseLeave={() => {
                          setShowTieredPricingTable(false);
                        }}
                      >
                        Fee
                      </Link>
                    ) : (
                      <NumberFormat thousandsGroupStyle="thousand" value={0} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} />
                    )
                  ) : (
                    //if its paid by the pm show zero fees
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={(flatFeeSchedule.PaidBy === PaidByEnum.Payer ? flatFeeSchedule.Amount / 100 : 0) ?? 0}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid container className={classes.gridRows}>
              <Grid item xs={leftColumnSize}>
                <Typography id="bold-header">Total Amount</Typography>
              </Grid>
              <Grid item xs={rightColumnSize}>
                <Typography>
                  {props.selectedAutoPay.Type === "Variable" ? (
                    <>Full Balance</>
                  ) : (
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={(props.selectedAutoPay.FixedAmount ?? 0) / 100 + +((flatFeeSchedule.PaidBy === PaidByEnum.Payer ? flatFeeSchedule.Amount / 100 : 0) ?? 0)}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  )}

                  {props.selectedAutoPay.Type === "Variable" && (flatFeeSchedule.PaidBy === PaidByEnum.Pm || tieredFeeSchedule.PaidBy === PaidByEnum.Payer) ? (
                    <>
                      {" "}
                      +{" "}
                      {tieredFeeSchedule.AmountTiers ? (
                        <Link
                          className={classes.termsAndConditionLink}
                          onMouseEnter={() => {
                            setShowTieredPricingTable(true);
                          }}
                          onMouseLeave={() => {
                            setShowTieredPricingTable(false);
                          }}
                        >
                          Fee
                        </Link>
                      ) : (
                        <NumberFormat thousandsGroupStyle="thousand" value={flatFeeSchedule.Amount / 100 ?? 0} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} />
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Typography>
              </Grid>
            </Grid>

            <Divider />

            <Box className="additional-info" sx={{ marginTop: "10px", ".MuiTypography-root": { color: "#D3D3D3 !important" } }}>
              <Grid container className={classes.gridRows}>
                <Grid item xs={leftColumnSize}>
                  <Typography id="bold-header">Payment Method</Typography>
                </Grid>
                <Grid item xs={rightColumnSize}>
                  <Typography>
                    {(
                      isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0
                        ? skipUnSkipAutopayDetails?.ScheduledAutoPayDetails?.[0].FromPayMethod?.AccountType
                        : props.selectedAutoPay?.ScheduledAutoPayDetails?.[0].FromPayMethod?.AccountType
                    )
                      ? "Bank Account"
                      : (isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0
                          ? skipUnSkipAutopayDetails?.ScheduledAutoPayDetails?.[0].FromPayMethod?.CardType
                          : props.selectedAutoPay?.ScheduledAutoPayDetails?.[0].FromPayMethod?.CardType) === "DB"
                      ? "Debit Card"
                      : "Credit Card"}
                    :{" "}
                    {isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0
                      ? skipUnSkipAutopayDetails?.ScheduledAutoPayDetails?.[0].FromPayMethod?.AccountLastFour
                      : props.selectedAutoPay?.ScheduledAutoPayDetails?.[0].FromPayMethod?.AccountLastFour}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container className={classes.gridRows}>
                <Grid item xs={leftColumnSize}>
                  <Typography id="bold-header">End Date</Typography>
                </Grid>
                <Grid item xs={rightColumnSize}>
                  <Typography>{props.selectedAutoPay.EndDate ? `${moment(props.selectedAutoPay.EndDate).format("MM/DD/Y")}` : "Indefinite"}</Typography>
                </Grid>
              </Grid>
              <Grid container className={classes.gridRows}>
                <Grid item xs={leftColumnSize}>
                  <Typography id="bold-header">Frequency </Typography>
                </Grid>
                <Grid item xs={rightColumnSize}>
                  <Typography>
                    {(isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.Frequency : props.selectedAutoPay?.Frequency) === "M"
                      ? "Monthly"
                      : (isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.Frequency : props.selectedAutoPay?.Frequency) === "Q"
                      ? "Quarterly"
                      : (isSkipAutoPaySuccess && skipUnSkipAutopayDetails && Object.keys(skipUnSkipAutopayDetails).length !== 0 ? skipUnSkipAutopayDetails?.Frequency : props.selectedAutoPay?.Frequency) === "S"
                      ? "Semi-Annually"
                      : "Annually"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Stack className="action-buttons" direction="row" alignItems="center" spacing={2} style={{ marginTop: "30px" }}>
            <Grid item sx={matchesMobile ? { display: "flex", justifyContent: "center", width: "100%" } : {}}>
              <Button variant="contained" id="cancel-autopay-btn" onClick={() => handleOpenCancelModal()}>
                Cancel Recurring Payment
              </Button>
            </Grid>
            <Modal open={isOpenCancelModal} onClose={handleCloseCancelModal} hideBackdrop={true}>
              <Box sx={style}>
                <Grid container style={{ marginBottom: "20px" }}>
                  <Grid item xs={2}></Grid>
                  <Grid item xs={8}>
                    <Typography
                      id="payment-method-title"
                      style={{
                        fontSize: "20px",
                        lineHeight: "21px",
                      }}
                    >
                      Confirm Cancel{" "}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <img src={CloseModalIcon} alt="close" style={{ cursor: "pointer" }} onClick={handleCloseCancelModal} />
                  </Grid>
                </Grid>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} style={{ marginBottom: "20px" }}>
                  <Typography
                    style={{
                      fontFamily: '"Work Sans",Open Sans',
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: "18px",
                      lineHeight: "21px",
                      textAlign: "center",
                    }}
                  >
                    You are about to cancel this recurring payment. No further payments will be created. Any processing payments will continue <br /> to process.
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Typography>Are you sure you want to proceed?</Typography>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} style={{ marginTop: "35px", marginBottom: "10px" }}>
                  <div>
                    <Button
                      id="yes"
                      onClick={() => {
                        handleCancelAutoPay();
                      }}
                      variant="contained"
                      style={{
                        background: "#A2DBFA",
                        borderRadius: "24px",
                        width: "87px",
                        height: "45px",
                        fontFamily: "'Open Sans', sans-serif",
                        fontSize: "18px",
                        fontWeight: 700,
                        fontStyle: "normal",
                        textTransform: "capitalize",
                      }}
                    >
                      Yes
                    </Button>
                  </div>
                  <div>
                    <Button
                      onClick={handleCloseCancelModal}
                      variant="contained"
                      style={{
                        background: "#39A2DB",
                        borderRadius: "24px",
                        width: "87px",
                        height: "45px",
                        fontFamily: "'Open Sans', sans-serif",
                        fontSize: "18px",
                        fontWeight: 700,
                        fontStyle: "normal",
                        textTransform: "capitalize",
                      }}
                    >
                      No
                    </Button>
                  </div>
                </Stack>
              </Box>
            </Modal>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default ViewRecurringDetailsModal;
